/**
 * The **modelled searchlight** each beam leaves — the hardware half of AD-017
 * §10, split out of `searchlight-beams.ts` because a beam is light in air and a
 * projector is a machine, and the two share nothing but a direction.
 *
 * The model comes from `tools/blender/searchlight.py` through `bun run
 * enhanced`, and its three nodes articulate: `base` stands still, `yoke` yaws
 * in azimuth, `drum` pitches in elevation. Everything below is about driving
 * those three from one aim vector, and about reading the two numbers the beam
 * needs — where the elevation pivot is, and how far ahead of it the glass sits
 * — off the model rather than out of a constant here.
 */

import * as THREE from "three/webgpu";
import { loadEnhancedModel } from "./enhanced-model.ts";

const scratchMatrix = new THREE.Matrix4();
const scratchDrum = new THREE.Matrix4();
const scratchPosition = new THREE.Vector3();
const scratchQuaternion = new THREE.Quaternion();
const scratchYaw = new THREE.Quaternion();
const scratchEuler = new THREE.Euler();
const scratchScale = new THREE.Vector3();
const scratchAxis = new THREE.Vector3();
/** The axis a yoke yaws about — the world vertical through its own emplacement. */
const WORLD_UP = new THREE.Vector3(0, 1, 0);

/** How far over a shot-out projector lies. See `pushWreck` for why it is not 90 degrees. */
const WRECK_CANT_RAD = (74 * Math.PI) / 180;

/**
 * The modelled searchlight under each beam, and where it comes from.
 *
 * The original draws **nothing** at a flak battery (AD-010 §6: no model, no
 * health, nothing in `aa.c` or the `.WLD` data places one), which was invisible
 * by day and fine — but AD-017's beams gave every battery a bright line whose
 * apex sat on bare ground, and a light with no lamp reads as a rendering bug,
 * not a battery (user, 2026-08-23: "there's zero flak models at the origin").
 * So the extension that created the visible origin supplies the hardware for
 * it, and only at night — by day the battery stays authentically invisible.
 *
 * The lamp is a real model rather than a pair of boxes: `tools/blender/
 * searchlight.py` builds a period 90 cm projector — tripod foot, U-yoke,
 * drum with a lens face — in `BARON.PAL`'s steel ramp, and `bun run enhanced`
 * writes it here. It is the one model in `data/enhanced` measured from the
 * period rather than from a `.TBL`, because there is no `.TBL` to measure.
 */
const SEARCHLIGHT_MODEL_PATH = "models/searchlight.glb";

/**
 * The three nodes the generator writes, in the order they are instanced.
 *
 * `base` never moves, `yoke` yaws in azimuth and `drum` pitches in elevation,
 * which is the whole articulation: a light visibly points where its beam
 * points, through a sweep and through a 40 deg/s pounce alike.
 */
const MOUNT_NODE_NAMES = ["base", "yoke", "drum"] as const;

/**
 * The pool of mounts: one instanced mesh per articulated piece, so six lights
 * cost three draw calls rather than eighteen.
 *
 * Instancing an *articulated* prop works because each piece's motion is a rigid
 * transform of its own node — the yoke a yaw about the emplacement's vertical,
 * the drum that same yaw plus a pitch about its trunnion — and a rigid
 * transform is exactly what an instance matrix carries. Nothing here needs a
 * scene graph, and a `THREE.Group` per light would have bought only the ability
 * to write the same two angles in two places.
 *
 * The two numbers the app needs from the model are **read off the model**
 * rather than restated here: the trunnion height is the `drum` node's own
 * translation, and the aperture is that mesh's own forward bound. Change the
 * generator and the beam still leaves the glass.
 */
export class SearchlightMounts {
  /** Add all three to the scene; they publish and hide together. */
  readonly meshes: readonly THREE.InstancedMesh[];
  /** Height of the elevation pivot above the emplacement, in metres. */
  readonly trunnionHeightM: number;
  /** How far the lens sits ahead of that pivot along the aim, in metres. */
  readonly lensForwardM: number;
  private readonly capacity: number;
  private written = 0;

  constructor(model: THREE.Object3D, capacity: number) {
    this.capacity = capacity;
    const parts = MOUNT_NODE_NAMES.map((name) => {
      const node = model.getObjectByName(name);
      if (!(node instanceof THREE.Mesh)) throw new Error(`searchlight model has no '${name}' mesh node`);
      return node;
    });
    const [, , drum] = parts;
    if (drum === undefined) throw new Error("searchlight model has no 'drum' mesh node");
    this.trunnionHeightM = drum.position.y;
    drum.geometry.computeBoundingBox();
    // The model faces world `-Z` at rest (glTF turns the generator's `+Y` into
    // it), so the aperture is the *most negative* Z the drum reaches.
    this.lensForwardM = -(drum.geometry.boundingBox?.min.z ?? 0);
    this.meshes = parts.map((part) => {
      const mesh = new THREE.InstancedMesh(part.geometry, part.material as THREE.Material, capacity);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // Same call every pool in this renderer makes: the batteries stand
      // kilometres apart and a bound computed over one unit prop would cull
      // the whole mesh permanently.
      mesh.frustumCulled = false;
      mesh.count = 0;
      mesh.visible = false;
      return mesh;
    });
  }

  begin(): void {
    this.written = 0;
  }

  /**
   * Points one mount along `(dirX, dirY, dirZ)`, standing at `(x, y, z)`.
   *
   * Azimuth is `flight/searchlights.ts`'s own — measured from north (`-Z`)
   * towards east (`+X`) — so the hardware and the sweep that drives it cannot
   * disagree about which way the light is looking.
   */
  push(x: number, y: number, z: number, dirX: number, dirY: number, dirZ: number): boolean {
    if (this.written >= this.capacity) return false;
    const azimuth = Math.atan2(dirX, -dirZ);
    const elevation = Math.asin(Math.min(1, Math.max(-1, dirY)));
    scratchQuaternion.setFromEuler(scratchEuler.set(elevation, -azimuth, 0, "YXZ"));
    scratchScale.setScalar(1);
    const [base, yoke, drum] = this.meshes;
    if (!base || !yoke || !drum) return false;
    base.setMatrixAt(this.written, scratchMatrix.makeTranslation(x, y, z));
    scratchPosition.set(x, y, z);
    scratchYaw.setFromAxisAngle(WORLD_UP, -azimuth);
    yoke.setMatrixAt(this.written, scratchMatrix.compose(scratchPosition, scratchYaw, scratchScale));
    // The trunnion sits on the yaw axis itself, so the yaw leaves the offset
    // alone and the drum's own position is simply the emplacement lifted.
    scratchPosition.set(x, y + this.trunnionHeightM, z);
    drum.setMatrixAt(this.written, scratchMatrix.compose(scratchPosition, scratchQuaternion, scratchScale));
    this.written += 1;
    return true;
  }

  /**
   * Places one *wrecked* mount: the whole projector knocked over onto its side
   * at `(x, y, z)`, canted along `bearingRad`.
   *
   * A wreck is a **rigid topple of the assembly**, not a pose of its
   * articulation, and that is what makes it cost nothing. The three pieces are
   * instanced with arbitrary matrices anyway, so one transform — rotate the
   * whole thing about a horizontal axis through its own feet, then translate to
   * the emplacement — applied to each piece's *rest* offset lays the lamp on
   * the ground with its yoke and drum still attached to it. No second model, no
   * second material, no per-instance tint: the same three draw calls the live
   * mounts already make.
   *
   * `WRECK_CANT_RAD` is short of a right angle on purpose. Flat on its face the
   * drum disappears into the ground plane and the wreck reads as nothing at
   * all; at 74 degrees the barrel is still catching the sky, which at night is
   * the difference between "the light is off" and "the light is *down*".
   */
  pushWreck(x: number, y: number, z: number, bearingRad: number): boolean {
    if (this.written >= this.capacity) return false;
    const [base, yoke, drum] = this.meshes;
    if (!base || !yoke || !drum) return false;
    // The axis it fell over: horizontal, square to the bearing it fell along.
    scratchAxis.set(Math.cos(bearingRad), 0, -Math.sin(bearingRad));
    scratchQuaternion.setFromAxisAngle(scratchAxis, WRECK_CANT_RAD);
    scratchPosition.set(x, y, z);
    scratchScale.setScalar(1);
    scratchMatrix.compose(scratchPosition, scratchQuaternion, scratchScale);
    base.setMatrixAt(this.written, scratchMatrix);
    yoke.setMatrixAt(this.written, scratchMatrix);
    // The drum rides the same topple, from its own place on the yaw axis.
    scratchDrum.makeTranslation(0, this.trunnionHeightM, 0).premultiply(scratchMatrix);
    drum.setMatrixAt(this.written, scratchDrum);
    this.written += 1;
    return true;
  }

  end(): void {
    for (const mesh of this.meshes) {
      mesh.count = this.written;
      mesh.visible = this.written > 0;
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      mesh.geometry.dispose();
      // Freeing the material is what releases this render object's bindings
      // under the WebGPU backend (2026-08-19 world-stability lesson). The three
      // pieces share one material, and `Material.dispose` is idempotent.
      (mesh.material as THREE.Material).dispose();
    }
  }
}

/**
 * Fetches the searchlight model and builds a pool of mounts from it, or
 * `undefined` when there is no model to build one from.
 *
 * `data/enhanced` is optional and is written by a separate `bun run enhanced`
 * step, so its absence is a normal state and not an error — the same stance
 * `enhanced-catalog.ts` takes for the aircraft. Without it a night mission
 * simply draws beams and no hardware, which is exactly what AD-017 shipped
 * before this addendum.
 */
export async function loadSearchlightMounts(capacity: number): Promise<SearchlightMounts | undefined> {
  try {
    return new SearchlightMounts(await loadEnhancedModel(SEARCHLIGHT_MODEL_PATH), capacity);
  } catch {
    return undefined;
  }
}
